import { ArrowRight, Search, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { useSiteSearch } from "./useSiteSearch";

/**
 * 岛内搜索：点击放大镜展开输入框并聚焦，回车跳到素材库搜索结果，Esc 或点关闭收起。
 * 在素材库页面打开时会带上当前关键词。
 */
export function SiteHeaderSearch({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);
  const search = useSiteSearch();

  const setOpenState = (next: boolean) => {
    setOpen(next);
    onOpenChange(next);
  };

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      return;
    }
    // 关闭后按钮才重新挂载，所以在这里把焦点还给放大镜。
    if (restoreFocusRef.current) toggleRef.current?.focus();
    restoreFocusRef.current = false;
  }, [open]);

  const openSearch = () => {
    setValue(params.get("q") ?? "");
    setOpenState(true);
  };

  const close = () => {
    restoreFocusRef.current = true;
    setOpenState(false);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    search(value);
    setOpenState(false);
  };

  if (!open) {
    return (
      <button ref={toggleRef} type="button" className="site-icon-button" onClick={openSearch} aria-label="搜索素材库" title="搜索素材库">
        <Search size={17} strokeWidth={1.9} />
      </button>
    );
  }

  return (
    <form className="site-search" role="search" onSubmit={submit} onKeyDown={(event) => event.key === "Escape" && close()}>
      <Search size={16} strokeWidth={1.9} aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="搜索模型、材质、纹理…"
        aria-label="搜索素材库"
        enterKeyHint="search"
      />
      <button type="submit" className="site-search-submit" aria-label="搜索"><ArrowRight size={15} strokeWidth={2.2} /></button>
      <button type="button" className="site-search-close" onClick={close} aria-label="关闭搜索"><X size={15} /></button>
    </form>
  );
}
